import React, { useState } from "react";
import { Link } from "react-router-dom";

// ─────────────────────────────────────────────
//  JSX Fundamentals & Expressions
//  Chapter 1 · Intro
//  Author: Ajao Hazzan · Full-Stack Software Engineer
// ─────────────────────────────────────────────

const PRIMARY = "#61DAFB";
const BG = "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1600&q=80";

/* ── Code snippets stored as template literals ── */
const CODE_0 = `// JSX
const element = <h1 className="title">Hello, World!</h1>;

// What Babel compiles it to:
const element = React.createElement(
  'h1',
  { className: 'title' },
  'Hello, World!'
);`;

const CODE_1 = `// ✅ Correct — single root with Fragment
function App() {
  return (
    <>
      <Header />
      <Main />
      <Footer />
    </>
  );
}

// ❌ Error — multiple root elements
function App() {
  return (
    <Header />
    <Main />    // SyntaxError
  );
}`;

const CODE_2 = `const user   = { name: 'Alice', age: 30, isPremium: true };
const items  = ['Apple', 'Banana', 'Cherry'];
const getGreeting = (name) => \`Hello, \${name}!\`;

function Profile() {
  return (
    <div>
      {/* String expression */}
      <h1>{getGreeting(user.name)}</h1>

      {/* Arithmetic */}
      <p>Born in: {new Date().getFullYear() - user.age}</p>

      {/* Ternary — conditional */}
      <span>{user.isPremium ? '⭐ Premium' : 'Free'}</span>

      {/* Logical && — short-circuit render */}
      {user.isPremium && <Badge label="Pro Member" />}

      {/* Array map — render lists */}
      <ul>
        {items.map((item, i) => <li key={i}>{item}</li>)}
      </ul>

      {/* Object — inline styles */}
      <div style={{ color: 'blue', fontWeight: 'bold' }}>
        Styled text
      </div>
    </div>
  );
}`;

const CODE_3 = `// Short form — most common
function List({ items }) {
  return (
    <>
      <h2>Items</h2>
      <ul>{items.map(i => <li>{i}</li>)}</ul>
    </>
  );
}

// Long form — when you need key on Fragment
function DefinitionList({ terms }) {
  return (
    <dl>
      {terms.map(term => (
        <React.Fragment key={term.id}>
          <dt>{term.word}</dt>
          <dd>{term.definition}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}`;


/* ── Styles ── */
const styles = {
  page:    { minHeight: "100vh", backgroundImage: `linear-gradient(rgba(10,12,30,0.94),rgba(10,12,30,0.97)),url(${BG})`, backgroundSize: "cover", backgroundAttachment: "fixed", color: "#fff", fontFamily: "'Segoe UI',sans-serif", paddingBottom: "60px" },
  header:  { background: "rgba(10,12,30,0.95)", borderBottom: "1px solid rgba(97,218,251,0.15)", padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", position: "sticky", top: 0, zIndex: 100 },
  badge:   { background: "rgba(97,218,251,0.12)", border: "1px solid rgba(97,218,251,0.3)", color: PRIMARY, padding: "4px 14px", borderRadius: "999px", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.06em" },
  backBtn: { background: "rgba(97,218,251,0.1)", border: "1px solid rgba(97,218,251,0.25)", color: PRIMARY, padding: "8px 18px", borderRadius: "999px", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px" },
  hero:    { padding: "48px 32px 24px", maxWidth: "900px", margin: "0 auto" },
  title:   { fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 800, color: PRIMARY, marginBottom: "10px", lineHeight: 1.2 },
  desc:    { color: "rgba(255,255,255,0.55)", fontSize: "1rem", lineHeight: 1.7, maxWidth: "680px", marginBottom: "32px" },
  content: { maxWidth: "900px", margin: "0 auto", padding: "0 32px 40px" },
  section: { marginBottom: "36px" },
  sh:      { fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: "10px", borderLeft: "3px solid " + PRIMARY, paddingLeft: "12px" },
  body:    { color: "rgba(255,255,255,0.72)", lineHeight: 1.85, fontSize: "0.95rem", marginBottom: "6px" },
  pre:     { background: "#0D0F1A", border: "1px solid rgba(97,218,251,0.15)", borderLeft: "3px solid " + PRIMARY, borderRadius: "10px", padding: "20px 22px", overflowX: "auto", margin: "14px 0", fontSize: "0.83rem", lineHeight: 1.7, color: "#CDD6F4", fontFamily: "Courier New,monospace", whiteSpace: "pre" },
  qBox:    { background: "rgba(97,218,251,0.04)", border: "1px solid rgba(97,218,251,0.12)", borderRadius: "10px", padding: "18px 22px", marginTop: "8px" },
  qTitle:  { color: PRIMARY, fontWeight: 700, fontSize: "0.82rem", letterSpacing: "0.08em", marginBottom: "10px" },
  qItem:   { color: "rgba(255,255,255,0.65)", lineHeight: 1.8, fontSize: "0.92rem", marginBottom: "4px" },
  footer:  { maxWidth: "900px", margin: "0 auto", padding: "24px 32px 0", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
  credit:  { color: "rgba(255,255,255,0.25)", fontSize: "0.75rem" },
};

/* ── Component ── */
export default function JSXFundamentalsAndExpressions() {
  const [copied, setCopied] = useState(null);

  function copyCode(codeStr, idx) {
    navigator.clipboard?.writeText(codeStr).then(() => {
      setCopied(idx);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div style={styles.page}>

      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Dashboard</Link>
          <span style={styles.badge}>Chapter 1 · Intro</span>
        </div>
        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
          By <strong style={{ color: PRIMARY }}>Ajao Hazzan</strong> · Full-Stack Software Engineer
        </span>
      </header>

      {/* Hero */}
      <div style={styles.hero}>
        <h1 style={styles.title}>JSX Fundamentals & Expressions</h1>
        <p style={styles.desc}>Master JSX syntax — the markup language that powers all React components.</p>
      </div>

      {/* Content */}
      <div style={styles.content}>

      /* ── What is JSX? ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>What is JSX?</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "JSX (JavaScript XML) is a syntax extension for JavaScript that looks like HTML but compiles to JavaScript function calls. It was created by the React team to make component code more readable and expressive. JSX is <b>not required</b> to use React, but virtually everyone uses it because it makes UI structure obvious at a glance." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_0, 0)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 0 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_0)}</pre>
        </div>
      </div>

      /* ── JSX Rules You Must Know ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>JSX Rules You Must Know</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "<b>Rule 1:</b> Return a single root element (wrap siblings in a &lt;div&gt; or Fragment)&lt;br/&gt;<b>Rule 2:</b> Close all tags — even self-closing ones: &amp;lt;img /&amp;gt; &amp;lt;input /&amp;gt;&lt;br/&gt;<b>Rule 3:</b> Use camelCase for HTML attributes: className, onClick, htmlFor&lt;br/&gt;<b>Rule 4:</b> JavaScript expressions go in curly braces { }" }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_1, 1)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 1 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_1)}</pre>
        </div>
      </div>

      /* ── Expressions in JSX ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Expressions in JSX</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Any valid JavaScript expression can be embedded in JSX using curly braces. Expressions are evaluated and their result is rendered." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_2, 2)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 2 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_2)}</pre>
        </div>
      </div>

      /* ── JSX Fragments ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>JSX Fragments</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "React.Fragment (shorthand &lt;&gt;&lt;/&gt;) lets you return multiple elements without adding an extra DOM node. Use the long form when you need to pass a key prop (e.g. in lists)." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_3, 3)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 3 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_3)}</pre>
        </div>
      </div>

      /* ── Practice Questions ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Practice Questions</h2>
        <div style={styles.qBox}>
          <div style={styles.qTitle}>📝 PRACTICE QUESTIONS</div>
          <div style={styles.qItem}>✦ 1. Why do we write className instead of class in JSX?</div>
          <div style={styles.qItem}>✦ 2. What is a Fragment and when do you need the long form?</div>
          <div style={styles.qItem}>✦ 3. Can you put an if statement directly inside JSX? What do you use instead?</div>
          <div style={styles.qItem}>✦ 4. What happens if you put an object directly inside JSX: &#123; myObject &#125;?</div>
          <div style={styles.qItem}>✦ 5. Rewrite this using JSX: React.createElement(\'p\', &#123;id: \'intro\'&#125;, \'Hello\')</div>
        </div>
      </div>

        {/* Footer */}
        <div style={styles.footer}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Back to React Dashboard</Link>
          <span style={styles.credit}>Mastering React · © 2024 Ajao Hazzan</span>
        </div>
      </div>

    </div>
  );
}
